import { Icon } from '@iconify/react';
import { Box, CircularProgress, Snackbar, Tooltip, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import TypingIndicator from '@components/Ai-participant/chat/TypingIndicator';
import ErrorBoundary from '@components/ErrorBoundary';
import { AddToReportButton } from '@components/common/AddToReportButton';
import { MagicSVG } from '@components/icon/svg';
import { useFetchChatHistoryById } from '@hooks/getQueries';
import { useLikeDislikeChat, useSendAndGetMessage } from '@hooks/postQueries';
import ChatInput from './ChatInput';
import DeepSearchBanner from './DeepSearchBanner';
function convertMarkdownToBasicHtml(markdownText) {
  if (!markdownText) return '';

  return markdownText
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // bold markdown
    .replace(/\n\n/g, '<br><br>'); // double newlines only
}

import { ChatContent, ErrorMessage, SparkleIcon, WelcomeMessage } from './ChatStyles';

import ChatBubble from './ChatBubble';

function ChatConversation({
  id,
  chatId = '',
  newChatId,
  resetCount,
  finalSelectedTranscripts,
  setFinalSelectedTranscripts,
  setSelected,
  isDeepSearchMode,
  setIsDeepSearchMode,
  getTranscriptName,
  openTranscriptSelectionModal,
  setOpenTranscriptSelectionModal,
  isTranscriptListUpdated,
  setIsTranscriptListUpdated,
  toggle,
  onChatIdChange,
  currentChatId,
  setCurrentChatId,
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const title = 'Chat with Data';
  const [isPanelOpen, setIsPanelOpen] = useState(true);
  const [copied, setCopied] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [feedbackType, setFeedbackType] = useState('');
  const [transcriptError, setTranscriptError] = useState(null);
  const bottomRef = useRef(null);
  const [localMessageList, setLocalMessageList] = useState([]);

  // Normalize selection to a flat array of IDs
  const selectedIds = useMemo(() => {
    const arr = Array.isArray(finalSelectedTranscripts) ? finalSelectedTranscripts.flat() : [];
    return arr.map((v) => (typeof v === 'object' && v !== null ? v.id : v)).filter(Boolean);
  }, [finalSelectedTranscripts]);

  const { data: messageData, mutateAsync, isPending } = useSendAndGetMessage();

  const isFirstLoadPending =
    isPending && localMessageList.length == 0 && newChatId === 'new-conversation';
  const { mutateAsync: likeDislikeChat, isPending: isLikingDisliking } = useLikeDislikeChat();
  const {
    data: chatHistoryDetails,
    isFetching: isFetchingChatDetails,
    error: chatHistoryError,
    isError: isChatHistoryError,
  } = useFetchChatHistoryById(chatId, newChatId, {
    onSuccess: (data) => {
      if (Array.isArray(data?.turns)) {
        // setLocalMessageList(data.turns);
      }
    },
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!chatHistoryDetails || !Array.isArray(chatHistoryDetails.turns)) return;
    setLocalMessageList(chatHistoryDetails.turns);

    // Persist IDs directly from API
    const newSelected = chatHistoryDetails?.selected_transcripts || [];
    setFinalSelectedTranscripts(newSelected);
    setIsDeepSearchMode(chatHistoryDetails?.deep_search_enabled);
    setSelected(newSelected);
  }, [chatHistoryDetails]);

  // Clear message list when navigating to new conversation
  useEffect(() => {
    if (newChatId === 'new-conversation') {
      setLocalMessageList([]);
      setCurrentChatId('');
    }
  }, [newChatId]);

  useEffect(() => {
    // Reset current chat ID when starting a new conversation
    if (newChatId === 'new-conversation') {
      setCurrentChatId('');
      // Remove the automatic mutateAsync call
    } else if (chatId && chatId !== '-') {
      setCurrentChatId(chatId);
    }
  }, []);

  useEffect(() => {
    // Only reset and create new chat when explicitly starting a new conversation
    if (newChatId === 'new-conversation') {
      setLocalMessageList([]);
      setCurrentChatId('');
      // Remove the automatic mutateAsync call
    }
  }, [resetCount]);

  useLayoutEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [localMessageList.length]);

  const handleInputValueChange = (e) => setInputValue(e.target.value);

  const getChatIdForRequest = () => {
    if (currentChatId) return currentChatId;
    if (chatId && chatId !== '-') return chatId;
    return '';
  };

  const handleSend = async () => {
    const trimmedValue = inputValue.trim();
    if (!trimmedValue) return;

    const userMessage = { role: 'user', content: trimmedValue };

    setInputValue('');
    setTranscriptError(null); // Clear any previous errors

    setLocalMessageList((prev) => [...prev, userMessage]);

    try {
      const basePayload = {
        message: trimmedValue,
        study_id: id,
        search_mode: isDeepSearchMode ? 'deep_search' : 'file_search',
        cache_status: isTranscriptListUpdated ? false : true,
        chat_id: getChatIdForRequest(),
      };

      const payload = {
        ...basePayload,
        ...(isDeepSearchMode && selectedIds.length > 0 ? { transcript_ids: selectedIds } : {}), // omit transcript_ids to default to all transcripts
      };

      const data = await mutateAsync(payload);

      // Check for transcript load error
      if (data?.metadata?.error === 'transcript_load_failed') {
        setTranscriptError(data.message || 'No transcripts available for this study');
        setLocalMessageList((prev) => prev.slice(0, -1));
        return;
      }

      const assistantMessage = data?.chat_history
        ?.slice()
        .reverse()
        .find((msg) => msg.role === 'assistant');

      if (assistantMessage) {
        setLocalMessageList((prev) => {
          const alreadyExists = prev.some((m) => m.id === assistantMessage.id);
          return alreadyExists ? prev : [...prev, assistantMessage];
        });
      }

      if (data?.chat_id && data?.chat_id !== currentChatId) {
        setCurrentChatId(data.chat_id);

        if (onChatIdChange) {
          onChatIdChange(data.chat_id);
        }
        if (newChatId === 'new-conversation' || !currentChatId) {
          queryClient.invalidateQueries({ queryKey: ['chat-history', id] });
        }
        if (newChatId === 'new-conversation') {
          navigate(`/interview/${id}/chat-with-data/${data.chat_id}`, { replace: true });
        }
      }

      setIsTranscriptListUpdated(false);
    } catch (error) {
      console.error('Failed to send message:', error);
      setLocalMessageList((prev) => prev.slice(0, -1));
      setTranscriptError('Failed to send message. Please try again.');
    }
  };

  const handleLikeDislikeChat = async ({ type, turnId }) => {
    const payload = {
      chatId: currentChatId || chatId,
      turnId,
      feedback: { feedback: type },
    };

    try {
      setFeedbackType(type);
      await likeDislikeChat(payload);
      if (messageData?.chat_id && !chatId) {
        setLocalMessageList((prev) =>
          prev.map((msg) => (msg.id === turnId ? { ...msg, feedback: type } : msg)),
        );
      }
    } catch (err) {
      console.error('Failed to like/dislike.', err?.detail || err);
    }
  };

  const handleSwitchChange = (e) => {
    if (!isDeepSearchMode) {
      setIsDeepSearchMode(e.target.checked);
      setOpenTranscriptSelectionModal(true);
    } else {
      setIsDeepSearchMode(e.target.checked);
    }
  };
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  const messageList =
    localMessageList.length > 0
      ? localMessageList
      : newChatId !== 'new-conversation' && newChatId !== '-'
        ? chatHistoryDetails?.turns
        : [];

  const isEmptyState =
    !isFetchingChatDetails && (!messageList || messageList.length === 0) && !isPending;

  const isChatNotFound = isChatHistoryError && chatHistoryError?.status === 404;
  const hasOtherError = isChatHistoryError && chatHistoryError?.status !== 404;
  const hasTranscriptError = transcriptError && !isPending;

  const CenteredInput = (
    <ChatInput
      value={inputValue}
      onChange={(e) => setInputValue(e.target.value)}
      onSend={handleSend}
      onKeyDown={handleKeyDown}
      isPending={isPending}
      isDeepSearchMode={isDeepSearchMode}
      onToggleDeepSearch={handleSwitchChange}
      hasTranscriptError={hasTranscriptError}
      variant="centered"
    />
  );

  const BottomInput = (
    <ChatInput
      value={inputValue}
      onChange={handleInputValueChange}
      onSend={handleSend}
      onKeyDown={handleKeyDown}
      isPending={isPending}
      isDeepSearchMode={isDeepSearchMode}
      onToggleDeepSearch={handleSwitchChange}
      hasTranscriptError={hasTranscriptError}
      variant="bottom"
    />
  );

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        p: 3,
        ml: '280px',
        position: 'relative',
        height: 'calc(100vh - 140px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {isDeepSearchMode && !openTranscriptSelectionModal && (
        <DeepSearchBanner
          isPanelOpen={isPanelOpen}
          onTogglePanel={() => setIsPanelOpen((p) => !p)}
          selectedIds={selectedIds}
          getTranscriptName={getTranscriptName}
          onRemoveTranscript={toggle}
          onOpenSelection={() => setOpenTranscriptSelectionModal(true)}
        />
      )}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          mb: isEmptyState ? 0 : '90px',
        }}
      >
        {isFetchingChatDetails ? (
          <Box display="flex" justifyContent="center" mt={15}>
            <CircularProgress />
          </Box>
        ) : hasTranscriptError ? (
          <Box
            height="100%"
            display="flex"
            flexDirection={'column'}
            alignItems="center"
            justifyContent="center"
          >
            <ChatContent>
              <ErrorMessage>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#fff3e0',
                    mb: 2,
                  }}
                >
                  <Icon icon="mdi:file-document-alert" width={32} height={32} color="#ff9800" />
                </Box>
                <Typography variant="h5" gutterBottom color="text.primary">
                  No Transcripts Available
                </Typography>
                <Typography variant="body1" color="text.secondary" mb={3}>
                  {transcriptError}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please upload transcripts to start chatting with your data.
                </Typography>
              </ErrorMessage>
            </ChatContent>
          </Box>
        ) : isChatNotFound ? (
          <Box
            height="100%"
            display="flex"
            flexDirection={'column'}
            alignItems="center"
            justifyContent="center"
          >
            <ChatContent>
              <ErrorMessage>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#ffebee',
                    mb: 2,
                  }}
                >
                  <Icon icon="mdi:chat-remove" width={32} height={32} color="#f44336" />
                </Box>
                <Typography variant="h5" gutterBottom color="text.primary">
                  {chatHistoryError?.detail}
                </Typography>
                <Typography variant="body1" color="text.secondary" mb={1}>
                  This conversation has been deleted or doesn&apos;t exist anymore.
                </Typography>
              </ErrorMessage>
            </ChatContent>
          </Box>
        ) : hasOtherError ? (
          <Box
            height="100%"
            display="flex"
            flexDirection={'column'}
            alignItems="center"
            justifyContent="center"
          >
            <ChatContent>
              <ErrorMessage>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#fff3e0',
                    mb: 2,
                  }}
                >
                  <Icon icon="mdi:alert-circle" width={32} height={32} color="#ff9800" />
                </Box>
                <Typography variant="h5" gutterBottom color="text.primary">
                  {chatHistoryError?.detail}
                </Typography>
                <Typography variant="body1" color="text.secondary" mb={3}>
                  We couldn&apos;t load this conversation.
                </Typography>
              </ErrorMessage>
            </ChatContent>
          </Box>
        ) : isEmptyState && (newChatId === 'new-conversation' || newChatId === '-') ? (
          <Box
            height="100%"
            display="flex"
            flexDirection={'column'}
            alignItems="center"
            justifyContent="center"
          >
            <ChatContent>
              <WelcomeMessage>
                <SparkleIcon>
                  <MagicSVG />
                </SparkleIcon>
                <Typography variant="h5" gutterBottom>
                  Discover insights from your study data faster than ever!
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Directly ask about your study results or explore insights by filtering specific
                  respondent groups.
                </Typography>
                <Typography sx={{ fontSize: '22px', textAlign: 'center' }} mt={2}>
                  What can I help with?
                </Typography>
                <Box mt={2}>{CenteredInput}</Box>
              </WelcomeMessage>
            </ChatContent>
          </Box>
        ) : (
          <Box p={2} mt={isDeepSearchMode ? 10 : 2}>
            {messageList?.map((msg, idx) => {
              const rawMarkdown = msg.content.replace(/\[ENSURING\]|\[ENSURED\]/g, '');

              const htmlContent = convertMarkdownToBasicHtml(rawMarkdown);

              return (
                <Box key={idx} position="relative">
                  <ChatBubble
                    id={id}
                    role={msg.role}
                    content={msg.content}
                    sources={msg.sources}
                    getTranscriptName={getTranscriptName}
                  />
                  {msg.role === 'assistant' && msg.content && !isPending && (
                    <Box mt={-1} pl={1}>
                      <ErrorBoundary FallbackComponent={() => null}>
                        <Box display="flex" alignItems="center" gap={2}>
                          {isLikingDisliking && feedbackType === 'like' ? (
                            <CircularProgress size={18} thickness={4} />
                          ) : (
                            <Tooltip title="Like this response" placement="top">
                              <Icon
                                icon={msg.feedback === 'like' ? 'bxs:like' : 'iconamoon:like'}
                                width={22}
                                height={22}
                                color={msg.feedback === 'like' ? '#7d5ae8' : undefined}
                                style={{ cursor: 'pointer' }}
                                onClick={() =>
                                  handleLikeDislikeChat({ type: 'like', turnId: msg.id })
                                }
                              />
                            </Tooltip>
                          )}
                          {isLikingDisliking && feedbackType === 'dislike' ? (
                            <CircularProgress size={18} thickness={4} />
                          ) : (
                            <Tooltip title="Dislike this response" placement="top">
                              <Icon
                                icon={msg.feedback === 'dislike' ? 'bxs:dislike' : 'bx:dislike'}
                                width={22}
                                height={22}
                                color={msg.feedback === 'dislike' ? '#7d5ae8' : undefined}
                                style={{ cursor: 'pointer' }}
                                onClick={() =>
                                  handleLikeDislikeChat({ type: 'dislike', turnId: msg.id })
                                }
                              />
                            </Tooltip>
                          )}
                          <Tooltip title="Copy Response" placement="top">
                            <Icon
                              icon="tabler:copy"
                              width={22}
                              height={22}
                              style={{ cursor: 'pointer' }}
                              onClick={() => {
                                navigator.clipboard.writeText(msg.content || '');
                                setCopied(true);
                                setTimeout(() => setCopied(false), 2000);
                              }}
                            />
                          </Tooltip>
                          <Tooltip
                            title="You can add this chat response to the custom report"
                            placement="top"
                          >
                            <AddToReportButton
                              name={title}
                              description={`<strong>Question: ${localMessageList[idx - 1]?.content}</strong><br/><br/> ${htmlContent}`}
                            />
                          </Tooltip>
                        </Box>
                      </ErrorBoundary>
                    </Box>
                  )}
                </Box>
              );
            })}
            {isFirstLoadPending ? (
              <Box
                display="flex"
                justifyContent="center"
                mt={2}
                height={'50vh'}
                alignItems={'center'}
                flexDirection={'column'}
              >
                <div className="cc_loader"></div>
                <Typography mt={1}>Initiating...</Typography>
              </Box>
            ) : (
              isPending && (
                <Box mt={2}>
                  <TypingIndicator />
                </Box>
              )
            )}
            <div ref={bottomRef} />
          </Box>
        )}
      </Box>

      {!isEmptyState && !isFirstLoadPending && !hasTranscriptError && BottomInput}

      <Snackbar
        open={copied}
        message="Copied to clipboard!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={2000}
      />
    </Box>
  );
}

export default ChatConversation;
